import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Locate, Store as StoreIcon } from "lucide-react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import type { Marker as LeafletMarker } from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/features/auth/AuthContext";
import { opsApi, type Store, type StoreWrite } from "@/features/operations/api";
import { api } from "@/features/auth/api";
import { isGoogleMapsConfigured, reverseGeocode } from "@/features/maps/google";

/**
 * Small helper: keeps the Leaflet map view centred on the current lat/lng
 * whenever the form values change (e.g. after "Detect My Location").
 */
function RecenterMap({ lat, lng }: { lat: number; lng: number }) {
    const map = useMap();
    useEffect(() => {
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            map.setView([lat, lng], Math.max(map.getZoom(), 15), { animate: true });
        }
    }, [lat, lng, map]);
    return null;
}

/**
 * Store admin manages their OWN store here. Super admins see a selector at top
 * (fallback — they mostly use /admin/stores which is the full-network view).
 */
export default function AdminStoreSettings() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const [form, setForm] = useState<StoreWrite | null>(null);
    const [storeId, setStoreId] = useState<string>("");
    const [detecting, setDetecting] = useState(false);
    const markerRef = useRef<LeafletMarker | null>(null);

    /**
     * Reverse-geocode the given coords and fill the address fields IF the
     * Google Maps key is configured. Silent no-op otherwise so the manual
     * form still works.
     */
    const fillAddressFrom = async (lat: number, lng: number) => {
        if (!isGoogleMapsConfigured()) return;
        try {
            const place = await reverseGeocode(lat, lng);
            if (place) {
                setForm((prev) =>
                    prev
                        ? {
                              ...prev,
                              address_line1: place.line1 || prev.address_line1,
                              address_line2: place.line2 || prev.address_line2,
                              city: place.city || prev.city,
                              state: place.state || prev.state,
                              pincode: place.pincode || prev.pincode,
                          }
                        : prev,
                );
            }
        } catch {
            /* best effort */
        }
    };

    const detectLocation = () => {
        if (!("geolocation" in navigator)) {
            toast.error("Location not supported on this device");
            return;
        }
        setDetecting(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                setForm((prev) => (prev ? { ...prev, geo: { lat, lng } } : prev));
                await fillAddressFrom(lat, lng);
                setDetecting(false);
                toast.success("Location detected — drag the pin to fine-tune");
            },
            (err) => {
                setDetecting(false);
                const msg =
                    err.code === err.PERMISSION_DENIED
                        ? "Location permission denied — you can still set lat/lng manually."
                        : "Couldn't get your location. Please try again.";
                toast.error(msg);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
        );
    };

    // Fetch a single store: store_admin uses their own; super uses the picked one.
    const stores = useQuery({
        queryKey: ["admin", "stores"],
        queryFn: opsApi.listStores,
        enabled: user?.role === "super_admin" || user?.role === "admin",
    });

    const myStore = useQuery<Store>({
        queryKey: ["admin", "store", user?.store_id ?? "self"],
        queryFn: async () => {
            if (user?.store_id) {
                return (await api.get<Store>(`/admin/stores/${user.store_id}`)).data;
            }
            throw new Error("no store id");
        },
        enabled: Boolean(user?.store_id),
    });

    useEffect(() => {
        const active =
            myStore.data ?? stores.data?.items.find((s) => s.id === storeId);
        if (active) {
            setForm({
                name: active.name,
                code: active.code,
                address_line1: active.address_line1,
                address_line2: active.address_line2,
                city: active.city,
                state: active.state,
                pincode: active.pincode,
                phone: active.phone,
                geo: active.geo,
                service_radius_km: active.service_radius_km,
                is_active: active.is_active,
            });
            setStoreId(active.id);
        }
    }, [myStore.data, stores.data, storeId]);

    const updateMut = useMutation({
        mutationFn: (p: StoreWrite) => opsApi.updateStore(storeId, p),
        onSuccess: () => {
            toast.success("Store settings saved");
            qc.invalidateQueries({ queryKey: ["admin", "store"] });
            qc.invalidateQueries({ queryKey: ["admin", "stores"] });
        },
        onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Failed to save"),
    });

    if (!user) return null;

    return (
        <div data-testid="admin-store-settings-page" className="space-y-6">
            <header className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-600 text-white">
                    <StoreIcon className="h-5 w-5" />
                </span>
                <div>
                    <h1 className="text-2xl font-extrabold">Store settings</h1>
                    <p className="text-sm text-muted-foreground">
                        Update address, GPS coordinates and service radius for your store.
                    </p>
                </div>
            </header>

            {(user.role === "super_admin" || user.role === "admin") && (
                <div className="rounded-xl border border-border/60 bg-white p-4">
                    <Label htmlFor="store-picker">Editing store</Label>
                    <select
                        id="store-picker"
                        className="mt-2 w-full rounded-md border border-border bg-white p-2 text-sm"
                        value={storeId}
                        onChange={(e) => setStoreId(e.target.value)}
                    >
                        <option value="">Select a store…</option>
                        {stores.data?.items.map((s) => (
                            <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                        ))}
                    </select>
                </div>
            )}

            {!form || !storeId ? (
                <p className="rounded-xl border border-dashed border-border/60 bg-white p-10 text-center text-sm text-muted-foreground">
                    {user.role === "store_admin"
                        ? "No store attached to your account. Contact your super admin."
                        : "Pick a store above to edit its settings."}
                </p>
            ) : (
                <form
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        updateMut.mutate(form);
                    }}
                >
                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <Label htmlFor="ss-name">Name</Label>
                            <Input id="ss-name" data-testid="store-settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div>
                            <Label htmlFor="ss-code">Code</Label>
                            <Input id="ss-code" data-testid="store-settings-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="ss-addr">Address line 1</Label>
                        <Input id="ss-addr" data-testid="store-settings-address" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} required />
                    </div>
                    <div className="grid gap-3 md:grid-cols-3">
                        <div>
                            <Label htmlFor="ss-city">City</Label>
                            <Input id="ss-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
                        </div>
                        <div>
                            <Label htmlFor="ss-state">State</Label>
                            <Input id="ss-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required />
                        </div>
                        <div>
                            <Label htmlFor="ss-pin">Pincode</Label>
                            <Input id="ss-pin" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} required />
                        </div>
                    </div>
                    <div className="rounded-2xl border border-border/60 bg-white p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-bold">Store location on map</p>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={detectLocation}
                                disabled={detecting}
                                data-testid="store-settings-detect-location"
                            >
                                <Locate className={`mr-1 h-4 w-4 ${detecting ? "animate-spin" : ""}`} />
                                {detecting ? "Detecting…" : "Detect my current location"}
                            </Button>
                        </div>
                        <div className="h-64 w-full overflow-hidden rounded-xl" data-testid="store-settings-map">
                            <MapContainer
                                center={[form.geo.lat || 0, form.geo.lng || 0]}
                                zoom={form.geo.lat && form.geo.lng ? 15 : 3}
                                scrollWheelZoom
                                style={{ height: "100%", width: "100%" }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <RecenterMap lat={form.geo.lat} lng={form.geo.lng} />
                                <Marker
                                    draggable
                                    position={[form.geo.lat || 0, form.geo.lng || 0]}
                                    eventHandlers={{
                                        dragend: async () => {
                                            const m = markerRef.current;
                                            if (!m) return;
                                            const ll = m.getLatLng();
                                            setForm((prev) =>
                                                prev
                                                    ? { ...prev, geo: { lat: ll.lat, lng: ll.lng } }
                                                    : prev,
                                            );
                                            await fillAddressFrom(ll.lat, ll.lng);
                                        },
                                    }}
                                    ref={(instance) => {
                                        markerRef.current = instance as unknown as LeafletMarker;
                                    }}
                                />
                            </MapContainer>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                            Drag the pin to fine-tune. Coordinates update automatically.
                        </p>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                        <div>
                            <Label htmlFor="ss-lat">Lat</Label>
                            <Input id="ss-lat" type="number" step="0.000001" value={form.geo.lat} onChange={(e) => setForm({ ...form, geo: { ...form.geo, lat: Number(e.target.value) } })} required />
                        </div>
                        <div>
                            <Label htmlFor="ss-lng">Lng</Label>
                            <Input id="ss-lng" type="number" step="0.000001" value={form.geo.lng} onChange={(e) => setForm({ ...form, geo: { ...form.geo, lng: Number(e.target.value) } })} required />
                        </div>
                        <div>
                            <Label htmlFor="ss-r">Radius (km)</Label>
                            <Input id="ss-r" type="number" step="0.5" min="0.5" value={form.service_radius_km} onChange={(e) => setForm({ ...form, service_radius_km: Number(e.target.value) })} required />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="ss-phone">Contact phone</Label>
                        <Input id="ss-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div className="flex items-center gap-2">
                        <input id="ss-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                        <Label htmlFor="ss-active" className="cursor-pointer">Store is active (accepts orders)</Label>
                    </div>
                    <Button type="submit" className="bg-brand text-white" data-testid="store-settings-save" disabled={updateMut.isPending}>
                        {updateMut.isPending ? "Saving…" : "Save changes"}
                    </Button>
                </form>
            )}
        </div>
    );
}
